/**
 * §6.8 Subscription Plans — three white-label pricing cards.
 *
 * 06/2026 — CTA buttons now open the shared <WlCheckoutModal/> so
 * white-label visitors get the same inline discount-code + Stripe
 * Checkout flow as the /pricing "Commercial Decision Center".
 */

import React, { useState } from 'react'
import clsx from 'clsx'
import { Check, Sparkles, ArrowRight } from 'lucide-react'
import { Container, Eyebrow, SectionHeading, GradientButton, GhostButton } from '@/components/landing/design-system'
import { PLANS } from '../data'
import WlCheckoutModal from '@/components/landing/pricing/WlCheckoutModal'
import { WL_PLAN_BY_ID } from '@/lib/pricing'

const COMMON_INCLUDES = [
  'All 12 platform modules',
  'Multi-tenant workspaces',
  'Custom domain + SSL',
  'AI Mentor (grounded)',
  'Trading Terminal + strategy',
  'Academy content + tracking',
  'Enterprise RBAC + audit log',
  'Priority support + onboarding',
]

// Marketing plan id → central pricing engine plan id
const PLAN_ID_MAP = {
  quarterly:  'quarterly',
  halfyearly: 'halfyearly',
  annual:     'annual',
}

export default function Subscription() {
  const [checkoutPlan, setCheckoutPlan] = useState(null)

  const openCheckout = (p) => {
    const planKey = PLAN_ID_MAP[p.id] || p.id
    const live = WL_PLAN_BY_ID[planKey]
    if (!live) return
    setCheckoutPlan({
      planId:       live.id,
      label:        `${p.label} License`,
      duration:     live.duration || p.period,
      amount_cents: live.amount_cents,
    })
  }

  return (
    <section id="launch-plans" className="relative py-24 md:py-28">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" />
      <Container size="wide" className="flex flex-col gap-10">
        <div className="flex flex-col items-center text-center gap-5 max-w-3xl mx-auto">
          <Eyebrow>Subscription Plans</Eyebrow>
          <SectionHeading align="center"
            title={<>Simple, predictable{' '}<span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">white-label pricing.</span></>}
            subtitle="Three straightforward plans. Every plan includes every module. Save more with longer commitments — cancel or downgrade any time."
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {PLANS.map((p) => (
            <div key={p.id}
              className={clsx(
                'relative rounded-3xl border p-6 md:p-7 backdrop-blur-xl overflow-hidden flex flex-col gap-5 transition-all',
                p.highlight
                  ? 'border-brand-gold/40 bg-gradient-to-b from-brand-gold/[0.08] to-white/[0.01] shadow-[0_20px_70px_-20px_rgba(255,193,7,0.35)]'
                  : 'border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.03]',
              )}
            >
              {p.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full border border-brand-gold/40 bg-brand-gold/20 backdrop-blur-md px-3 py-1 text-[10.5px] font-semibold uppercase tracking-wider text-brand-gold">
                  <Sparkles className="h-3 w-3" /> {p.note}
                </span>
              )}
              {p.highlight && (
                <div className="pointer-events-none absolute inset-0 opacity-70"
                  style={{ background: 'radial-gradient(60% 40% at 20% 0%, rgba(255,193,7,0.12), transparent 70%)' }} />
              )}

              <div className="relative flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-white uppercase tracking-wider">{p.label}</span>
                  {p.save && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-emerald-400">
                      {p.save}
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="text-4xl md:text-5xl font-semibold tracking-tight text-white">{p.price}</span>
                  <span className="text-[13px] text-zinc-500">{p.period}</span>
                </div>
                {!p.highlight && (
                  <div className="text-[12px] text-zinc-500">{p.note}</div>
                )}
              </div>

              <div className="relative flex flex-col gap-2">
                {COMMON_INCLUDES.map((inc) => (
                  <div key={inc} className="flex items-start gap-2 text-[13px] text-zinc-300">
                    <Check className="h-3.5 w-3.5 text-brand-gold mt-0.5 shrink-0" />
                    <span>{inc}</span>
                  </div>
                ))}
              </div>

              <div className="relative mt-auto">
                {p.highlight ? (
                  <GradientButton onClick={() => openCheckout(p)} size="lg" className="w-full justify-center">
                    Launch platform
                  </GradientButton>
                ) : (
                  <GhostButton onClick={() => openCheckout(p)} size="lg" trailingIcon className="w-full justify-center">
                    Choose {p.label.toLowerCase()}
                  </GhostButton>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl border border-white/10 bg-white/[0.02] backdrop-blur-xl px-5 md:px-6 py-4 flex flex-col md:flex-row items-start md:items-center gap-3">
          <span className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-emerald-400">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> billed via Stripe
          </span>
          <p className="text-sm text-zinc-300 leading-relaxed">
            Cards, GPay, ApplePay and Netbanking supported. Prorated upgrades any time.
            Enterprise plans (multi-region, custom SLA, dedicated success) — <a href="/contact" className="text-brand-gold font-semibold hover:brightness-125 inline-flex items-center gap-1">talk to sales <ArrowRight className="h-3 w-3" /></a>
          </p>
        </div>
      </Container>

      <WlCheckoutModal
        open={!!checkoutPlan}
        plan={checkoutPlan}
        onClose={() => setCheckoutPlan(null)}
      />
    </section>
  )
}
